/**
 * Resolves which leaderboard row represents the signed-in caller, and whether
 * that row sits within the currently-loaded page slice. Shared by the global
 * (`useWorldCupLeaderboardData`) and squad (`useWorldCupGroupDetailData`) adapter
 * hooks so the always-visible "your standing" bar behaves identically in both.
 *
 * The caller resolves the two candidate rows up front:
 *  - `onPageEntry`  — the caller's row found inside the rendered list, or null.
 *  - `offPageEntry` — the caller's row reconstructed from the server's
 *    `currentUser` payload when they're outside the loaded slice, or null.
 *
 * (The global hook appends the off-page row into its list upstream, so there it
 * passes the same instance as both candidates — `offPageEntry === onPageEntry`.)
 */

import type { LeaderboardEntry } from '../types/worldcup.types';

interface ResolveSelfRowParams {
  /** Raw API entries (pre-mapping) — only their `isCurrentUser` flag is read. */
  rawEntries: ReadonlyArray<{ isCurrentUser?: boolean }>;
  /** The caller's row found within the rendered list, or null. */
  onPageEntry: LeaderboardEntry | null;
  /** The caller's row reconstructed from the off-page server payload, or null. */
  offPageEntry: LeaderboardEntry | null;
}

interface SelfRow {
  /** The caller's row wherever it sits, or null when they have no row. */
  currentUserEntry: LeaderboardEntry | null;
  /** True when the caller's row is within the loaded page slice. */
  isCurrentUserOnPage: boolean;
}

export function resolveLeaderboardSelfRow({
  rawEntries,
  onPageEntry,
  offPageEntry,
}: ResolveSelfRowParams): SelfRow {
  const isCurrentUserOnPage = rawEntries.some((entry) => entry.isCurrentUser === true);
  return {
    currentUserEntry: isCurrentUserOnPage ? onPageEntry : offPageEntry,
    isCurrentUserOnPage,
  };
}
